import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconMoreHorizontal from '@/assets/icons/icon-more-horizontal.svg?react';
import { LayerDropdown } from '@/components/layers/LayerDropdown/LayerDropdown';
import IconArrow from '@/assets/icons/icon-arrow.svg?react';
import { useLayer } from '@craftjs/layers';
import { ROOT_NODE } from '@craftjs/utils';
import useEditableLayer from '@/hooks/useEditableLayer';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useLayerNodeState } from '@/hooks/useLayerNodeState';
import { useCanvasFocus } from '@/context/CanvasFocusContext';
import { shouldIgnoreLayerRowDoubleClick } from '../layerRowUtils';
import { Layer } from '../Layer/Layer';
import IconImage from '@/assets/icons/icon-image.svg?react';
import IconText from '@/assets/icons/icon-text.svg?react';
import IconPlay from '@/assets/icons/icon-play.svg?react';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import IconSourceCode from '@/assets/icons/icon-source-code.svg?react';
import IconClock from '@/assets/icons/icon-clock.svg?react';
import IconView from '@/assets/icons/icon-view.svg?react';
import IconViewOffSlash from '@/assets/icons/icon-view-off-slash.svg?react';
import IconSquareLock from '@/assets/icons/icon-square-lock.svg?react';
import IconSquareUnlock from '@/assets/icons/icon-square-unlock.svg?react';

const defaultLayersData = [
  { text: 'Image', icon: IconText },
  { text: 'Text', icon: IconImage },
  { text: 'Video', icon: IconPlay },
  { text: 'Button', icon: IconMoreHorizontalSquare },
  { text: 'HTML', icon: IconSourceCode },
  { text: 'Countdown', icon: IconClock },
];

interface LayerCollapseProps {
  className: string;
  text: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  childrenContent?: React.ReactNode;
  depthLevel?: number;
  isOpen: boolean;
  isActive?: boolean;
  /** When true (and not active), render highlighted background for descendant selection */
  isHighlighted?: boolean;
  onSelect?: () => void;
  onToggleCollapse?: () => void;
  /**
   * Whether this layer has child nodes. If false, the caret/arrow is hidden but the
   * row keeps the same styling (useful to keep root "Landing Page" styling consistent
   * even when empty).
   */
  hasChildren?: boolean;
}

export const LayerCollapse: React.FC<LayerCollapseProps> = ({
  className = '',
  icon: Icon,
  text = '',
  hasChildren = true,
  childrenContent,
  depthLevel = 0,
  isOpen = true,
  isActive = true,
  isHighlighted = false,
  onSelect,
  onToggleCollapse,
}) => {
  // Fallback children if none provided (used only for stories/examples)
  const fallbackChildren = (
    <>
      {defaultLayersData.map(({ text, icon }, index) => (
        <Layer
          depthLevel={depthLevel ?? 0}
          key={index}
          className="max-w-[232px]"
          icon={icon}
          onClick={() => {}}
          text={text}
          type="default"
        />
      ))}
    </>
  );
  let id: string | undefined;
  let layerHeader: <B extends HTMLElement | React.ReactElement>(element: B) => B = (ref) => ref;
  let drag = <T extends HTMLElement>(ref: T) => ref;
  let isInsideCraft = true;

  try {
    const layer = useLayer();
    id = layer?.id ? String(layer.id) : undefined;
    layerHeader = layer?.connectors.layerHeader;
    drag = layer?.connectors.drag;
  } catch {
    isInsideCraft = false;
  }

  const { isHidden: isLayerHidden, isLocked: isLayerLocked } = useLayerNodeState(id);
  const [hidden, setHidden] = useState(false);
  const [locked, setLocked] = useState(false);
  const isRoot = id === ROOT_NODE;

  useEffect(() => {
    if (isInsideCraft && id) {
      setHidden(isLayerHidden);
      setLocked(isLayerLocked);
    }
  }, [isInsideCraft, id, isLayerHidden, isLayerLocked]);

  const {
    inputTitleRef,
    isEditing,
    textValue,
    handleOnBlur,
    handleOnChange,
    handleOnKeyUp,
    handleEnterEditMode,
  } = useEditableLayer({ text, id: id ?? '' });

  const { toggleVisibility, toggleLock } = useCraftHelper();
  const { focusCanvasOnSelectedNode } = useCanvasFocus();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleClick = () => {
    if (onSelect) onSelect();
  };
  // Indentation for collapses: base 8px + 12px per depth level (clamped)
  const normalizedDepth = Math.max(0, depthLevel ?? 0);
  const paddingStyle = {
    paddingLeft: 8 + 12 * normalizedDepth,
  };

  const handleToggleCollapse = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    if (onToggleCollapse) onToggleCollapse();
  };

  const handleToggleNodeVisibility = () => {
    if (!id) return;
    const newValue = toggleVisibility(id);
    setHidden(newValue);
  };

  const handleToggleLock = () => {
    const newLocked = !locked;
    setLocked(newLocked);
    if (id && isInsideCraft) {
      toggleLock(id);
    }
  };

  const handleRowDoubleClick = (e: React.MouseEvent) => {
    if (shouldIgnoreLayerRowDoubleClick(e)) return;
    e.stopPropagation();
    if (id) focusCanvasOnSelectedNode(id);
  };

  return (
    <div
      className={clsx('', className)}
      ref={(ref) => {
        if (isInsideCraft) {
          drag(ref as HTMLElement);
        }
      }}
    >
      {/* Wrapper owns the rounding; descendants drop rounding when highlighted */}
      <Collapsible
        className={clsx('overflow-hidden', { 'rounded-md': isActive })}
        onOpenChange={() => {}}
        open={isOpen}
      >
        <CollapsibleTrigger asChild>
          <div
            onClick={(event) => {
              event.stopPropagation();
              handleClick();
            }}
            onDoubleClick={handleRowDoubleClick}
            ref={(ref) => {
              if (isInsideCraft) {
                layerHeader(drag(ref as HTMLElement));
              }
            }}
            className={clsx(
              // Trigger does not control rounding; only wrapper is rounded
              'group animate-layer-enter flex w-full cursor-pointer items-center justify-between pr-2 transition-all aria-expanded:rounded-b-none motion-reduce:animate-none',
              // Hover rounding for rows outside the active container
              { 'hover:rounded-md hover:aria-expanded:rounded-b-md': !isHighlighted && !isActive },
              isActive
                ? 'bg-indigo-500 text-white'
                : isHighlighted
                  ? 'bg-indigo-50 text-neutral-500 hover:bg-indigo-100'
                  : 'bg-neutral-0 text-neutral-500 hover:bg-indigo-100',
              {
                // transient states can override highlight
                'bg-indigo-200': dropdownOpen,
                'bg-indigo-200 text-neutral-400 hover:bg-indigo-100': hidden === true && !isActive,
              },
            )}
            style={paddingStyle}
          >
            <div className="flex min-w-0 flex-grow-1 items-center gap-2">
              {/* Fixed gutter so caret width doesn't affect indentation alignment */}
              <div data-layer-caret className="w-5 flex-shrink-0">
                {hasChildren && (
                  <IconArrow
                    onClick={handleToggleCollapse}
                    data-qa-selector="layer-collapse-toggle"
                    className="h-4 w-4 flex-shrink-0 transition-all group-aria-expanded:rotate-90"
                  />
                )}
              </div>
              {(Icon || text !== '') && (
                <div
                  data-layer-rename
                  className="flex min-w-0 flex-grow-1 items-center gap-2 py-2"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    handleEnterEditMode();
                  }}
                >
                  {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                  <input
                    ref={inputTitleRef}
                    className={`${isEditing ? '' : 'hidden'} w-[70%] flex-grow-1 rounded border border-indigo-500 bg-white text-xs text-black`}
                    type="text"
                    value={textValue}
                    onBlur={handleOnBlur}
                    onChange={handleOnChange}
                    onKeyUp={handleOnKeyUp}
                  />
                  {!isEditing && <span className="block flex-grow-1 truncate text-xs">{text}</span>}
                </div>
              )}
            </div>

            {!isEditing && (
              <div data-layer-actions className="flex shrink-0 items-center gap-2">
                {!isRoot && (
                  <div
                    className={clsx('flex items-center gap-2 transition-all', {
                      'opacity-100': hidden || locked,
                      'opacity-0 group-hover:opacity-100': !(hidden || locked),
                    })}
                  >
                    <button
                      type="button"
                      className={clsx('transition-opacity', {
                        'opacity-100': hidden,
                        'opacity-0 group-hover:opacity-100': !hidden,
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isInsideCraft) handleToggleNodeVisibility();
                        else {
                          const nextHidden = !hidden;
                          setHidden(nextHidden);
                        }
                      }}
                      data-qa-selector="layer-visibility-toggle-button"
                    >
                      {hidden ? (
                        <IconViewOffSlash className="h-4 w-4 cursor-pointer" />
                      ) : (
                        <IconView className="h-4 w-4 cursor-pointer" />
                      )}
                    </button>
                    <button
                      type="button"
                      className={clsx('transition-opacity', {
                        'opacity-100': locked,
                        'opacity-0 group-hover:opacity-100': !locked,
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleLock();
                      }}
                      data-qa-selector="layer-lock-toggle-button"
                    >
                      {locked ? (
                        <IconSquareLock className="h-4 w-4 cursor-pointer" />
                      ) : (
                        <IconSquareUnlock className="h-4 w-4 cursor-pointer" />
                      )}
                    </button>
                  </div>
                )}
                <LayerDropdown
                  className=""
                  onClick={() => {}}
                  setDropdownOpen={setDropdownOpen}
                  childrenTrigger={<IconMoreHorizontal className="h-4 w-4" />}
                  dropdownOpen={dropdownOpen}
                  onEnterEditTitleMode={handleEnterEditMode}
                />
              </div>
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>{childrenContent ?? fallbackChildren}</CollapsibleContent>
      </Collapsible>
    </div>
  );
};
